import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { BEFORE_AFTER } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";

function CompareSlider({ pair }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(97, Math.max(3, ((clientX - r.left) / r.width) * 100)));
  };

  const onPointerDown = (e) => {
    update(e.clientX);
    const move = (ev) => update(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      onPointerDown={onPointerDown}
      className="relative h-[22rem] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl border border-stone2 sm:h-[28rem]"
    >
      <img src={pair.before} alt={`Antes: ${pair.title}`} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={pair.after} alt={`Depois: ${pair.title}`} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      </div>
      <span className="absolute left-4 top-4 rounded-full bg-pine-deep/80 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-ivory backdrop-blur">
        Depois
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-ivory/85 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-pine backdrop-blur">
        Antes
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-ivory shadow-[0_0_12px_rgba(0,0,0,0.4)]" />
        <span className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-ivory bg-pine text-ivory shadow-xl">
          <ChevronsLeftRight size={18} />
        </span>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [active, setActive] = useState(0);
  const pair = BEFORE_AFTER[active];

  return (
    <section id="trabalhos" data-testid="works-section" className="scroll-mt-24 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHead
          overline="Trabalhos Realizados"
          title="Antes e depois: o resultado fala por si"
          text="Arraste o cursor para comparar. Alguns exemplos do tipo de transformação que fazemos em terrenos, relvados e condomínios."
        />

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3">
            {BEFORE_AFTER.map((p, i) => (
              <button
                key={p.id}
                data-testid={`before-after-tab-${p.id}`}
                onClick={() => setActive(i)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                  i === active ? "bg-pine text-ivory" : "border border-stone2 bg-white text-moss hover:text-pine"
                }`}
              >
                {p.title}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={pair.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <CompareSlider pair={pair} />
                <p className="mt-5 text-sm font-medium text-moss">
                  {pair.title} · <span className="font-semibold text-lime-700">fotografias reais EcoJard</span>
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
